import { createNxCache } from "nx-cache";
import type { AiModelRecord } from "../cost/types.js";

const cache = createNxCache<Map<string, AiModelRecord>, { cachedAt: string; count: number }>();

/** Default in-memory catalog cache lifetime (24 hours). */
export const DEFAULT_CATALOG_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

/** @alias DEFAULT_CATALOG_CACHE_TTL_MS */
export const DEFAULT_MODEL_CACHE_TTL_MS = DEFAULT_CATALOG_CACHE_TTL_MS;

export function resolveCatalogCacheTtlMs(override?: number): number {
  if (override !== undefined) return override;
  const raw = process.env.AI_TOOLS_CACHE_TTL_MS;
  if (!raw) return DEFAULT_CATALOG_CACHE_TTL_MS;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : DEFAULT_CATALOG_CACHE_TTL_MS;
}

export type ModelCacheMetadata = { cachedAt: string; count: number };

export function readCachedModels(
  scopeKey: string,
  ttlMs = DEFAULT_CATALOG_CACHE_TTL_MS,
): Map<string, AiModelRecord> | null {
  const res = cache.read("ai-models", { appId: scopeKey });
  if (!res.found) return null;

  const cachedAt = res.metadata?.cachedAt;
  if (cachedAt) {
    const age = Date.now() - new Date(cachedAt).getTime();
    if (age > ttlMs) return null;
  } else if (res.meta.isExpired) {
    return null;
  }

  return res.value;
}

export function writeCachedModels(
  scopeKey: string,
  models: Map<string, AiModelRecord>,
  ttlMs = DEFAULT_CATALOG_CACHE_TTL_MS,
): void {
  cache.write("ai-models", models, {
    scope: { appId: scopeKey },
    ttlMs,
    metadata: { cachedAt: new Date().toISOString(), count: models.size },
  });
}

export function invalidateModelsCache(scopeKey: string): void {
  cache.resetKey("ai-models", { appId: scopeKey });
}
